import { ThemeEvents } from '@theme/events';

/**
 * Nova Saunas "Your Order Summary" block.
 *
 * Purely presentational: it never reads from or writes to the cart. All
 * pricing/savings/availability values are computed server-side in Liquid
 * (the same authoritative source used by the rest of the product page), so
 * on a variant change this component only swaps in the fresh markup the
 * theme's variant-picker already fetched — it does not recompute prices or
 * make any network requests of its own.
 *
 * Mirrors the same `variant:update` listen-and-swap pattern used by the
 * theme's own <product-price> component (assets/product-price.js).
 */
class OrderSummaryComponent extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  /**
   * @param {CustomEvent} event
   */
  handleVariantUpdate = (event) => {
    const { detail } = event;
    if (!detail?.data?.html) return;

    if (detail.data.newProduct) {
      this.dataset.productId = detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      this.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      // The event came from a different product's variant picker on this page
      // (e.g. a quick-add dialog) — ignore it.
      return;
    }

    const updatedComponent = detail.data.html.querySelector(
      `order-summary-component[data-block-id="${this.dataset.blockId}"]`
    );
    if (!updatedComponent) return;

    const newContent = updatedComponent.querySelector('[ref="content"]');
    const currentContent = this.querySelector('[ref="content"]');

    if (newContent && currentContent) {
      currentContent.replaceWith(newContent);
    }
  };
}

if (!customElements.get('order-summary-component')) {
  customElements.define('order-summary-component', OrderSummaryComponent);
}